/**
 * Board motion timings. MOTION is read at plan/sample time, so the dev
 * tuning panel can override the wave values live (applyMotionOverrides);
 * everything else treats it as constant.
 */

export type WaveEasingName = "prototype" | "easeOut" | "easeInOut" | "linear";
export const WAVE_EASINGS: WaveEasingName[] = [
  "prototype",
  "easeOut",
  "easeInOut",
  "linear",
];

export const MOTION_DEFAULTS = {
  popDelay: 100,
  popMs: 170,
  drawMs: 140,
  flashAt: 120,
  flashFrameMs: 35,
  nudgeMs: 260,
  nudgePx: 3,
  glideMs: 170,
  /** The streak wave starts this long after the move (and after the glide). */
  waveDelay: 180,
  /**
   * Build-up budget (ms): the n-1 gaps between pops always sum to this,
   * whatever the path length (streak and win separately).
   */
  waveBuildMs: 800,
  winBuildMs: 1100,
  /** Ritardando: last gap / first gap (gaps grow geometrically). */
  waveRitardando: 1.45,
  /** Per-gap clamp (ms) for very short or very long chains. */
  waveGapMin: 60,
  waveGapMax: 260,
  /** Crescendo: peaks ramp from the first node to the second-to-last. */
  waveCrescendoFrom: 1.15,
  waveCrescendoTo: 1.5,
  /** Duration of each non-final pop (ms). */
  waveMs: 300,
  /** Scale at the 75% keyframe of a non-final pop; 1 means no undershoot. */
  waveSettle: 1,
  waveEasing: "prototype" as WaveEasingName,
  /** Anticipation: a beat before the final node, which dips as it waits. */
  wavePause: 220,
  waveDip: 0.92,
  /** Release: the final node's bigger, longer pop with a soft settle. */
  waveFinalPeak: 1.9,
  winFinalPeak: 2.0,
  waveFinalMs: 650,
  /** Release keyframes as fractions of waveFinalMs: peak reached, hold ends. */
  waveFinalRise: 0.3,
  waveFinalHold: 0.55,
  shimmerLoopMs: 900,
  shimmerLoops: 3,
  shimmerDash: 24,
  coolMs: 420,
  coolGrey: "#8A8A8A",
  winDelay: 100,
  winEdgeMs: 160,
  /** The target's ring fills at the release, over this long. */
  winFillMs: 200,
  /** Options as nodes: old options fade out, new ones pop in, staggered. */
  optionFadeMs: 120,
  optionInDelay: 120,
  optionGap: 30,
  optionPopMs: 170,
  optionPeak: 1.2,
};

export type MotionConfig = typeof MOTION_DEFAULTS;

export const MOTION: MotionConfig = { ...MOTION_DEFAULTS };

/** The wave values the tuning panel exposes, with slider ranges. */
export const WAVE_TUNABLES: {
  key: keyof MotionConfig;
  label: string;
  min: number;
  max: number;
  step: number;
}[] = [
  { key: "waveBuildMs", label: "Streak build", min: 200, max: 2500, step: 50 },
  { key: "winBuildMs", label: "Win build", min: 200, max: 3000, step: 50 },
  {
    key: "waveRitardando",
    label: "Ritardando",
    min: 0.5,
    max: 3,
    step: 0.05,
  },
  { key: "waveGapMin", label: "Gap min", min: 0, max: 300, step: 5 },
  { key: "waveGapMax", label: "Gap max", min: 60, max: 800, step: 10 },
  {
    key: "waveCrescendoFrom",
    label: "Crescendo from",
    min: 1,
    max: 2.5,
    step: 0.01,
  },
  {
    key: "waveCrescendoTo",
    label: "Crescendo to",
    min: 1,
    max: 2.5,
    step: 0.01,
  },
  { key: "wavePause", label: "Pause", min: 0, max: 600, step: 10 },
  { key: "waveDip", label: "Dip", min: 0.7, max: 1, step: 0.01 },
  { key: "waveFinalPeak", label: "Final peak", min: 1, max: 3, step: 0.05 },
  { key: "winFinalPeak", label: "Win final peak", min: 1, max: 3, step: 0.05 },
  { key: "waveFinalMs", label: "Final hold", min: 200, max: 1500, step: 10 },
  { key: "waveMs", label: "Pop duration", min: 100, max: 800, step: 10 },
  { key: "waveSettle", label: "Settle", min: 0.8, max: 1.1, step: 0.01 },
  { key: "waveDelay", label: "Streak delay", min: 0, max: 600, step: 10 },
];

export type MotionOverrides = Partial<
  Record<keyof MotionConfig, number | string>
>;

/**
 * Reset MOTION to its defaults, then apply the overrides. Unknown keys (e.g.
 * a retired value still saved in localStorage) are ignored.
 */
export function applyMotionOverrides(o: MotionOverrides): void {
  const known = Object.fromEntries(
    Object.entries(o).filter(([k]) => k in MOTION_DEFAULTS),
  );
  Object.assign(MOTION, MOTION_DEFAULTS, known);
}

/** The current wave values as a single pasteable line. */
export function waveValuesLine(): string {
  const parts = WAVE_TUNABLES.map((t) => `${t.key}=${MOTION[t.key]}`);
  return `wave: ${parts.join(" ")} waveEasing=${MOTION.waveEasing}`;
}
